import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";
import Seo from "../components/Seo";
import { CATEGORIES, TYPES, AUDIENCES, filterProducts } from "../lib/store";
import ProductCard from "../components/ProductCard";

const TYPE_KEYS = ["rings", "chains", "earrings", "pendants", "bangles", "bracelets", "sets"];

const optionCls = "border border-[#d8cdb5] hover:border-[#a8842c] hover:bg-[#a8842c]/5 px-6 py-4 text-sm tracking-[0.14em] uppercase text-[#171310] transition-colors duration-300 text-left";

export default function Finder() {
  const [step, setStep] = useState(0);
  const [audience, setAudience] = useState(null);
  const [category, setCategory] = useState(null);
  const [type, setType] = useState(null);

  const filters = useMemo(() => {
    const f = {};
    if (audience) f.audience = audience;
    if (category) f.category = category;
    if (type) f.type = type;
    return f;
  }, [audience, category, type]);

  const results = useMemo(() => (step === 3 ? filterProducts(filters) : []), [step, filters]);

  const shopLink = `/shop?${new URLSearchParams(filters).toString()}`;
  const reset = () => { setStep(0); setAudience(null); setCategory(null); setType(null); };

  const steps = [
    {
      q: "Who is this piece for?",
      options: [...AUDIENCES.map((a) => ({ label: `For ${a.label}`, value: a.key })), { label: "No preference", value: null }],
      pick: (v) => { setAudience(v); setStep(1); },
    },
    {
      q: "Which world speaks to you?",
      options: [...CATEGORIES.map((c) => ({ label: c.label, value: c.key, hint: c.blurb })), { label: "Show me everything", value: null }],
      pick: (v) => { setCategory(v); setStep(2); },
    },
    {
      q: "What type of piece?",
      options: [...TYPE_KEYS.map((k) => ({ label: TYPES.find((t) => t.key === k)?.label || k, value: k })), { label: "Any type", value: null }],
      pick: (v) => { setType(v); setStep(3); },
    },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 min-h-[70vh]" data-testid="finder-page">
      <Seo title="Help Me Choose — Guided Jewelry Finder" description="Answer three quick questions and Mr. Regal will guide you to the right gold or diamond piece at Regal Jewels." path="/finder" />
      <div className="lg:flex lg:items-start lg:gap-12">
        <div className="hidden lg:block w-44 shrink-0">
          <img src="/mr-regal-idea.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" className="w-full h-auto opacity-90" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#a8842c]">Mr. Regal's Guided Finder</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#171310] mt-3" data-testid="finder-title">Help Me Choose</h1>

          {step < 3 ? (
            <>
              <p className="text-xs tracking-[0.25em] uppercase text-[#8f8571] mt-6" data-testid="finder-step">Step {step + 1} of 3</p>
              <h2 className="font-heading text-2xl text-[#171310] mt-2">{steps[step].q}</h2>
              <div className="grid sm:grid-cols-2 gap-3 mt-6 max-w-2xl">
                {steps[step].options.map((o) => (
                  <button key={o.label} onClick={() => steps[step].pick(o.value)} className={optionCls} data-testid={`finder-option-${(o.value || "any")}`}>
                    <span className="block">{o.label}</span>
                    {o.hint && <span className="block text-[10px] text-[#8f8571] normal-case tracking-normal mt-1">{o.hint}</span>}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 mt-8 text-[10px] tracking-[0.3em] uppercase text-[#7a7160] hover:text-[#a8842c] transition-colors" data-testid="finder-back">
                  <ArrowLeft size={12} /> Back
                </button>
              )}
            </>
          ) : (
            <div className="mt-6" data-testid="finder-results">
              <p className="text-sm text-[#5c5240]">
                {results.length > 0
                  ? `Mr. Regal found ${results.length} piece${results.length === 1 ? "" : "s"} for you.`
                  : "No exact matches — but the full collection is one step away."}
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                {results.length > 0 && (
                  <Link to={shopLink} className="inline-flex items-center gap-2 bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] text-[11px] tracking-[0.22em] uppercase px-6 py-3 transition-colors duration-300" data-testid="finder-viewall">
                    View all in the shop <ArrowRight size={13} />
                  </Link>
                )}
                <button onClick={reset} className="inline-flex items-center gap-2 border border-[#d8cdb5] hover:border-[#a8842c] text-[11px] tracking-[0.22em] uppercase px-6 py-3 text-[#171310] transition-colors duration-300" data-testid="finder-restart">
                  <RotateCcw size={13} /> Start over
                </button>
              </div>
              {results.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8 mt-10">
                  {results.slice(0, 12).map((p) => <ProductCard key={p.handle} product={p} />)}
                </div>
              )}
              {results.length === 0 && (
                <Link to="/shop" className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.22em] uppercase text-[#a8842c] border-b border-[#a8842c]/50 pb-1">
                  Browse the full collection <ArrowRight size={13} />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
